import axios from 'axios';
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Plus, Clock, X, Bell } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
const DAYS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
const MONTHS_FR = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const HOURS = Array.from({ length: 12 }, (_, i) => i + 7); // 7h à 18h

function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function CalendrierAdmin() {
  const { user } = useAuth();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [events, setEvents] = useState([]);
  const [rdvs, setRdvs] = useState([]);
  const [conges, setConges] = useState([]);
  const [horaire, setHoraire] = useState({ debut: '09:00', fin: '17:00' });
  const [selectedDay, setSelectedDay] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newEvent, setNewEvent] = useState({ titre: '', heure_debut: '09:00', heure_fin: '10:00', description: '', invites: [] });
  const [employees, setEmployees] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    if (!user?.employee_id) return;
    try {
      const eid = user.employee_id;
      const [evRes, rdvRes, congesRes, empRes, profilRes, invRes, lavageRes] = await Promise.all([
        axios.get(`${API_URL}/api/agenda-perso?employee_id=${eid}`).then(r => r.status === 200 ? r.data : []),
        axios.get(`${API_URL}/api/crm/rendez-vous?employe_id=${eid}`).then(r => r.status === 200 ? r.data : { rendez_vous: [] }),
        axios.get(`${API_URL}/api/conges?employee_id=${eid}&statut=approuve`).then(r => r.status === 200 ? r.data : []),
        axios.get(`${API_URL}/api/employees`).then(r => r.status === 200 ? r.data : { employees: [] }),
        axios.get(`${API_URL}/api/employees/${eid}`).then(r => r.status === 200 ? r.data : null),
        axios.get(`${API_URL}/api/agenda-perso/invitations/pending?employee_id=${eid}`).then(r => r.status === 200 ? r.data : []),
        axios.get(`${API_URL}/api/crm/routes-lavage`).catch(() => ({ data: { routes: [] } })),
      ]);
      setEvents(Array.isArray(evRes) ? evRes : evRes.events || []);
      setRdvs(rdvRes.rendez_vous || []);
      setConges(Array.isArray(congesRes) ? congesRes : congesRes.conges || []);
      setEmployees((empRes.employees || empRes || []).filter(e => e.est_actif !== false));
      setPendingInvites(Array.isArray(invRes) ? invRes : invRes.invitations || []);
      if (profilRes?.horaire_travail) setHoraire(profilRes.horaire_travail);

      // Routes de lavage où cet employé est assigné → ajouter comme événements spéciaux
      const allRoutes = lavageRes.data?.routes || [];
      const lavageEv = allRoutes
        .filter(route => (route.employes || []).some(e => e.id === eid) && route.statut !== 'annule')
        .map(route => ({
          id: `lavage-${route.id}`, date: route.date_route,
          titre: `🚿 Route de lavage — ${route.clients?.length || 0} client(s)`,
          heure_debut: '07:00', type: 'lavage', couleur: '#06b6d4',
        }));
      setEvents(prev => [...(Array.isArray(evRes) ? evRes : evRes.events || []), ...lavageEv]);
    } catch (e) { console.error(e); }
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const today = toDateStr(new Date());

  const getItemsForDay = (dateStr) => {
    const dayEvents = events.filter(e => (e.date || '').substring(0, 10) === dateStr);
    const dayRdvs = rdvs.filter(r => (r.date || '').substring(0, 10) === dateStr);
    const dayConges = conges.filter(c => {
      const d = (c.date_debut || '').substring(0, 10);
      const f = (c.date_fin || d).substring(0, 10);
      return dateStr >= d && dateStr <= f;
    });
    return { events: dayEvents, rdvs: dayRdvs, conges: dayConges };
  };

  // Vérifier les conflits avant création
  const checkConflicts = (date, heureDebut, heureFin) => {
    const items = getItemsForDay(date);
    const conflicts = [];
    [...items.events, ...items.rdvs].forEach(item => {
      const iStart = item.heure_debut || item.heure || '';
      const iEnd = item.heure_fin || '';
      if (iStart && heureDebut < (iEnd || '23:59') && heureFin > iStart) {
        conflicts.push(item.titre || item.client_nom || 'Événement');
      }
    });
    return conflicts;
  };

  const handleCreate = async () => {
    if (!newEvent.titre || !selectedDay) return;
    // Point 5: ajouter 1h off après l'événement
    const conflicts = checkConflicts(selectedDay, newEvent.heure_debut, newEvent.heure_fin);
    if (conflicts.length > 0) {
      toast.error(`Conflit d'horaire avec : ${conflicts.join(', ')}`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...newEvent,
        date: selectedDay,
        employe_id: user.employee_id,
        created_by: `${user.prenom} ${user.nom}`,
        invites: newEvent.invites.map(id => ({ employee_id: id, statut: 'en_attente' })),
        // Ajouter aussi le créateur s'il a coché "m'inclure"
        include_creator: newEvent.include_creator || false,
      };
      const r = await axios.post(`${API_URL}/api/agenda-perso`, payload);
      if (r.status === 200) {
        toast.success('Événement créé');
        setShowCreate(false);
        loadData();
      } else toast.error('Erreur création');
    } catch { toast.error('Erreur réseau'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 max-w-4xl mx-auto" data-testid="calendrier-admin">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900">Mon Calendrier</h1>
        {pendingInvites.length > 0 && (
          <Badge className="bg-amber-100 text-amber-700 flex items-center gap-1">
            <Bell className="w-3 h-3" /> {pendingInvites.length} invitation(s)
          </Badge>
        )}
      </div>

      {/* Calendrier mensuel */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <button onClick={prevMonth} className="p-2 hover:bg-slate-100 rounded-lg"><ChevronLeft className="w-5 h-5" /></button>
            <CardTitle className="text-base">{MONTHS_FR[month]} {year}</CardTitle>
            <button onClick={nextMonth} className="p-2 hover:bg-slate-100 rounded-lg"><ChevronRight className="w-5 h-5" /></button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-1 mb-2">
            {DAYS.map((d, i) => <div key={i} className="text-center text-xs font-medium text-slate-400 py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} />)}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
              const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const isToday = dateStr === today;
              const items = getItemsForDay(dateStr);
              const hasEvent = items.events.length > 0;
              const hasRdv = items.rdvs.length > 0;
              const hasConge = items.conges.length > 0;
              const isWeekend = new Date(year, month - 1, day).getDay() === 0 || new Date(year, month - 1, day).getDay() === 6;

              return (
                <div key={day}
                  onClick={() => { setSelectedDay(dateStr); setShowCreate(true); setNewEvent({ titre: '', heure_debut: '09:00', heure_fin: '10:00', description: '', invites: [], include_creator: true }); }}
                  className={`relative p-1 rounded-lg text-center cursor-pointer min-h-[60px] transition-colors
                    ${isToday ? 'ring-2 ring-blue-500 bg-blue-50' : ''}
                    ${isWeekend ? 'bg-slate-50' : 'hover:bg-blue-50'}
                    ${hasConge ? 'bg-amber-50' : ''}
                  `}
                >
                  <span className={`text-sm ${isToday ? 'font-bold text-blue-700' : isWeekend ? 'text-slate-400' : 'text-slate-700'}`}>{day}</span>
                  
                  {/* Barre horaire en gris pâle */}
                  {!isWeekend && !hasConge && (
                    <div className="mt-0.5 mx-auto w-full h-1 bg-slate-200 rounded-full" title={`Horaire: ${horaire.debut} - ${horaire.fin}`} />
                  )}

                  {/* Événements */}
                  <div className="mt-0.5 space-y-0.5">
                    {items.events.slice(0, 2).map((ev, i) => (
                      <div key={ev.id || ev._id || i} className="text-[9px] leading-tight truncate rounded px-0.5" style={{ background: ev.couleur || '#6366f1', color: 'white' }}>
                        {ev.heure_debut && <span>{ev.heure_debut} </span>}{ev.titre}
                      </div>
                    ))}
                    {items.rdvs.slice(0, 1).map((rv, i) => (
                      <div key={`r-${i}`} className="text-[9px] leading-tight truncate rounded px-0.5 bg-emerald-500 text-white">
                        {rv.heure && <span>{rv.heure} </span>}RDV {rv.client_nom}
                      </div>
                    ))}
                    {hasConge && <div className="text-[9px] rounded px-0.5 bg-amber-400 text-white">Congé</div>}
                    {(items.events.length > 2 || items.rdvs.length > 1) && (
                      <div className="text-[8px] text-slate-400">+{items.events.length + items.rdvs.length - 3}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Légende */}
          <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-100 text-xs text-slate-500">
            <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded bg-indigo-500" /> Événement</div>
            <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded bg-emerald-500" /> RDV soumission</div>
            <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded bg-amber-400" /> Congé</div>
            <div className="flex items-center gap-1"><div className="w-full h-1 bg-slate-200 rounded-full" style={{ width: 16 }} /> Horaire travail</div>
          </div>
        </CardContent>
      </Card>

      {/* Dialog création événement */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Créer un événement — {selectedDay}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Titre *</Label>
              <Input value={newEvent.titre} onChange={e => setNewEvent(p => ({ ...p, titre: e.target.value }))} placeholder="Réunion, formation..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Heure début</Label>
                <Input type="time" value={newEvent.heure_debut} onChange={e => {
                  const hd = e.target.value;
                  // Auto-calculer heure fin = début + 1h
                  const [h, m] = hd.split(':').map(Number);
                  const hf = `${String(Math.min(h + 1, 23)).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                  setNewEvent(p => ({ ...p, heure_debut: hd, heure_fin: hf }));
                }} />
              </div>
              <div>
                <Label className="text-xs">Heure fin</Label>
                <Input type="time" value={newEvent.heure_fin} onChange={e => setNewEvent(p => ({ ...p, heure_fin: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Input value={newEvent.description} onChange={e => setNewEvent(p => ({ ...p, description: e.target.value }))} placeholder="Détails..." />
            </div>
            {/* Inviter des employés */}
            <div>
              <Label className="text-xs">Inviter des employés</Label>
              <div className="max-h-32 overflow-y-auto border rounded-lg p-2 space-y-1">
                {employees.filter(e => e.id !== user?.employee_id).map(emp => (
                  <label key={emp.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1 rounded">
                    <input type="checkbox" checked={newEvent.invites.includes(emp.id)}
                      onChange={e => {
                        setNewEvent(p => ({
                          ...p,
                          invites: e.target.checked ? [...p.invites, emp.id] : p.invites.filter(id => id !== emp.id)
                        }));
                      }} className="rounded" />
                    {emp.prenom} {emp.nom}
                  </label>
                ))}
              </div>
            </div>
            {/* M'inclure aussi */}
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={newEvent.include_creator !== false}
                onChange={e => setNewEvent(p => ({ ...p, include_creator: e.target.checked }))} className="rounded" />
              Afficher dans mon calendrier aussi
            </label>
            {/* Conflits */}
            {selectedDay && newEvent.heure_debut && (() => {
              const conflicts = checkConflicts(selectedDay, newEvent.heure_debut, newEvent.heure_fin);
              return conflicts.length > 0 ? (
                <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700">
                  Conflit avec : {conflicts.join(', ')}
                </div>
              ) : null;
            })()}
            <Button onClick={handleCreate} disabled={saving || !newEvent.titre} className="w-full">
              {saving ? 'Création...' : 'Créer l\'événement'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
